import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import dayjs from "dayjs";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { apiCall } from "../config/api";

const AnnouncementsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  // form state
  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState({
    type: "topic-broadcast", // or send-to-all or template-broadcast
    topic: "app_updates",
    segment: "",
    templateKey: "",
    interestTopic: "app_updates",
    variables: "", // JSON string
    title: "",
    body: "",
    category: "system",
    priority: "normal",
    data: "", // JSON string
    status: "draft",
    scheduledAt: "", // ISO or empty
  });
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const status = statusFilter === "all" ? undefined : statusFilter;
      const response = await apiCall("/api/announcements", {
        method: "GET",
        params: { status, limit: 200 },
      });
      setItems(response.data.announcements || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [statusFilter, load]);

  const handleSubmit = async () => {
    try {
      const payload = {
        ...form,
        data: form.data ? JSON.parse(form.data) : {},
        variables: form.variables ? JSON.parse(form.variables) : {},
        segment: form.segment || null,
        scheduledAt: form.scheduledAt
          ? new Date(form.scheduledAt).toISOString()
          : null,
      };

      if (editingId) {
        await apiCall(`/api/announcements/${editingId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } else {
        await apiCall("/announcements", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }
      setOpenForm(false);
      setEditingId(null);
      await load();
    } catch (e) {
      alert("Hata: " + (e.message || e));
    }
  };

  const handleSubmitAndSendNow = async () => {
    try {
      const payload = {
        ...form,
        data: form.data ? JSON.parse(form.data) : {},
        variables: form.variables ? JSON.parse(form.variables) : {},
        segment: form.segment || null,
        scheduledAt: form.scheduledAt
          ? new Date(form.scheduledAt).toISOString()
          : null,
        sendNow: true,
      };

      if (editingId) {
        await apiCall(`/api/announcements/${editingId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } else {
        await apiCall("/announcements", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }
      setOpenForm(false);
      setEditingId(null);
      await load();
    } catch (e) {
      alert("Hata: " + (e.message || e));
    }
  };

  const resetForm = () => {
    setForm({
      type: "topic-broadcast",
      topic: "app_updates",
      segment: "",
      templateKey: "",
      interestTopic: "app_updates",
      variables: "",
      title: "",
      body: "",
      category: "system",
      priority: "normal",
      data: "",
      status: "draft",
      scheduledAt: "",
    });
    setEditingId(null);
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setForm({
      type: it.type || "topic-broadcast",
      topic: it.topic || "app_updates",
      segment: it.segment || "",
      templateKey: it.templateKey || "",
      interestTopic: it.interestTopic || "app_updates",
      variables: JSON.stringify(it.variables || {}, null, 2),
      title: it.title || "",
      body: it.body || "",
      category: it.category || "system",
      priority: it.priority || "normal",
      data: JSON.stringify(it.data || {}, null, 2),
      status: it.status || "draft",
      scheduledAt: it.scheduledAt
        ? dayjs(it.scheduledAt).format("YYYY-MM-DDTHH:mm")
        : "",
    });
    setOpenForm(true);
  };

  const handleBodyChange = (e) => {
    const val = e.target.value;
    const trimmed = (val || "").trim();
    const looksJson = trimmed.startsWith("{") || trimmed.startsWith("[");
    if (looksJson) {
      try {
        const parsed = JSON.parse(trimmed);
        const pretty = JSON.stringify(parsed, null, 2);
        setForm((f) => ({ ...f, data: pretty, body: "" }));
        return;
      } catch (_) {
        // fall through to plain text
      }
    }
    setForm((f) => ({ ...f, body: val }));
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="h4">🗓️ Planlı Duyurular</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Durum</InputLabel>
            <Select
              value={statusFilter}
              label="Durum"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">Hepsi</MenuItem>
              <MenuItem value="draft">Taslak</MenuItem>
              <MenuItem value="scheduled">Planlı</MenuItem>
              <MenuItem value="sent">Gönderildi</MenuItem>
              <MenuItem value="failed">Hatalı</MenuItem>
              <MenuItem value="processing">İşleniyor</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Yenile">
            <span>
              <IconButton onClick={load} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            onClick={() => {
              resetForm();
              setOpenForm(true);
            }}
          >
            Yeni Duyuru
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} elevation={3}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Oluşturma</TableCell>
              <TableCell>Tür</TableCell>
              <TableCell>Konu/Segment</TableCell>
              <TableCell>Başlık</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell align="right">İşlem</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((it) => (
              <TableRow key={it.id} hover>
                <TableCell>
                  {it.createdAt
                    ? dayjs(it.createdAt).format("YYYY-MM-DD HH:mm")
                    : "-"}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={it.type} />
                </TableCell>
                <TableCell>
                  {it.type === "topic-broadcast"
                    ? it.topic || "-"
                    : it.segment || "—"}
                </TableCell>
                <TableCell>{it.title}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={
                      it.status === "scheduled"
                        ? "info"
                        : it.status === "sent"
                        ? "success"
                        : it.status === "failed"
                        ? "error"
                        : it.status === "processing"
                        ? "warning"
                        : "default"
                    }
                    label={it.status}
                  />
                  {it.scheduledAt && (
                    <Typography variant="caption" sx={{ display: "block" }}>
                      ⏰ {dayjs(it.scheduledAt).format("YYYY-MM-DD HH:mm")}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Düzenle">
                    <IconButton onClick={() => startEdit(it)}>
                      <EditOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Şimdi Gönder">
                    <span>
                      <IconButton
                        disabled={loading || it.status === "sent"}
                        onClick={async () => {
                          await apiCall(`/api/announcements/${it.id}/send`, {
                            method: "POST",
                          });
                          await load();
                        }}
                      >
                        <SendIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Sil">
                    <IconButton
                      onClick={async () => {
                        await apiCall(`/api/announcements/${it.id}`, {
                          method: "DELETE",
                        });
                        await load();
                      }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Kayıt yok
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {editingId ? "Duyuruyu Düzenle" : "Yeni Duyuru"}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Tür</InputLabel>
              <Select
                value={form.type}
                label="Tür"
                onChange={(e) =>
                  setForm((f) => ({ ...f, type: e.target.value }))
                }
              >
                <MenuItem value="topic-broadcast">Topic</MenuItem>
                <MenuItem value="send-to-all">Herkese</MenuItem>
                <MenuItem value="template-broadcast">
                  Şablonlu (Çok Dilli)
                </MenuItem>
              </Select>
            </FormControl>
            {form.type === "topic-broadcast" ? (
              <FormControl fullWidth>
                <InputLabel>Topic</InputLabel>
                <Select
                  value={form.topic}
                  label="Topic"
                  onChange={(e) =>
                    setForm((f) => ({ ...f, topic: e.target.value }))
                  }
                >
                  <MenuItem value="app_updates">
                    Uygulama Güncellemeleri
                  </MenuItem>
                  <MenuItem value="maintenance_notifications">
                    Bakım Bildirimleri
                  </MenuItem>
                  <MenuItem value="security_alerts">
                    Güvenlik Uyarıları
                  </MenuItem>
                </Select>
              </FormControl>
            ) : form.type === "send-to-all" ? (
              <FormControl fullWidth>
                <InputLabel>Segment (opsiyonel)</InputLabel>
                <Select
                  value={form.segment}
                  label="Segment (opsiyonel)"
                  onChange={(e) =>
                    setForm((f) => ({ ...f, segment: e.target.value }))
                  }
                >
                  <MenuItem value="">Hepsi</MenuItem>
                  <MenuItem value="app_updates">
                    Uygulama Güncellemeleri
                  </MenuItem>
                  <MenuItem value="maintenance_notifications">
                    Bakım Bildirimleri
                  </MenuItem>
                  <MenuItem value="security_alerts">
                    Güvenlik Uyarıları
                  </MenuItem>
                </Select>
              </FormControl>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="Şablon Anahtarı (template key)"
                  value={form.templateKey}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, templateKey: e.target.value }))
                  }
                />
                <FormControl fullWidth>
                  <InputLabel>İlgi Topic'i</InputLabel>
                  <Select
                    value={form.interestTopic}
                    label="İlgi Topic'i"
                    onChange={(e) =>
                      setForm((f) => ({ ...f, interestTopic: e.target.value }))
                    }
                  >
                    <MenuItem value="app_updates">
                      Uygulama Güncellemeleri
                    </MenuItem>
                    <MenuItem value="maintenance_notifications">
                      Bakım Bildirimleri
                    </MenuItem>
                    <MenuItem value="security_alerts">
                      Güvenlik Uyarıları
                    </MenuItem>
                  </Select>
                </FormControl>
              </>
            )}
            <TextField
              fullWidth
              label="Başlık"
              value={form.title}
              onChange={(e) =>
                setForm((f) => ({ ...f, title: e.target.value }))
              }
            />
            <TextField
              fullWidth
              label="İçerik"
              multiline
              rows={3}
              value={form.body}
              onChange={handleBodyChange}
              helperText="Not: Buraya JSON yapıştırırsanız otomatik olarak 'Ek Veri (JSON)' alanına taşınır."
            />
            <FormControl fullWidth>
              <InputLabel>Kategori</InputLabel>
              <Select
                value={form.category}
                label="Kategori"
                onChange={(e) =>
                  setForm((f) => ({ ...f, category: e.target.value }))
                }
              >
                <MenuItem value="system">Sistem</MenuItem>
                <MenuItem value="social">Sosyal</MenuItem>
                <MenuItem value="location">Konum</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Öncelik</InputLabel>
              <Select
                value={form.priority}
                label="Öncelik"
                onChange={(e) =>
                  setForm((f) => ({ ...f, priority: e.target.value }))
                }
              >
                <MenuItem value="low">Düşük</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="high">Yüksek</MenuItem>
                <MenuItem value="urgent">Acil</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Ek Veri (JSON)"
              multiline
              rows={6}
              value={form.data}
              onChange={(e) => setForm((f) => ({ ...f, data: e.target.value }))}
            />
            {form.type === "template-broadcast" && (
              <TextField
                fullWidth
                label="Şablon Değişkenleri (JSON)"
                multiline
                rows={6}
                value={form.variables}
                onChange={(e) =>
                  setForm((f) => ({ ...f, variables: e.target.value }))
                }
                helperText='Örn: { "version": "2.0" }'
              />
            )}
            <FormControl fullWidth>
              <InputLabel>Durum</InputLabel>
              <Select
                value={form.status}
                label="Durum"
                onChange={(e) =>
                  setForm((f) => ({ ...f, status: e.target.value }))
                }
              >
                <MenuItem value="draft">Taslak</MenuItem>
                <MenuItem value="scheduled">Planlı</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              type="datetime-local"
              label="Gönderim Zamanı"
              InputLabelProps={{ shrink: true }}
              value={form.scheduledAt}
              onChange={(e) =>
                setForm((f) => ({ ...f, scheduledAt: e.target.value }))
              }
              helperText="Planlı gönderim için tarih/saat seçiniz"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>İptal</Button>
          <Button onClick={handleSubmitAndSendNow} color="secondary">
            Hemen Gönder
          </Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editingId ? "Güncelle" : "Oluştur"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AnnouncementsPage;
